import { authorizedRequest } from "../../../services/apiClient";
import { buildQueryString } from "../../../utils/queryParams";
import type { ApiPaginatedResponse, PaginationParams, SearchParams } from "@/types/api";

export interface ControlPoint {
  id: string;
  workspace_id: string | null;
  code: string;
  name?: string;
  region_id?: string | null;
  region_name?: string | null;
  province_id?: string | null;
  province_name?: string | null;
  municipality_id?: string | null;
  municipality_name?: string | null;
  srid: number;
  latitude: number;
  longitude: number;
  easting: number;
  northing: number;
  projected_srid?: number;
  projected_easting?: number | null;
  projected_northing?: number | null;
  source?: string;
  accuracy_m?: number | null;
  updated_at?: string;
}

export interface CreateControlPointPayload {
  code: string;
  name?: string;
  srid: number;
  province_id?: string | null;
  municipality_id?: string | null;
  latitude?: number;
  longitude?: number;
  latitude_dms?: string;
  longitude_dms?: string;
  projected_srid: number;
  projected_easting: number;
  projected_northing: number;
  source?: string;
  accuracy_m?: number | null;
}

export interface UpdateControlPointPayload {
  code: string;
  name?: string;
  srid: number;
  province_id?: string | null;
  municipality_id?: string | null;
  latitude?: number;
  longitude?: number;
  latitude_dms?: string;
  longitude_dms?: string;
  projected_srid: number;
  projected_easting: number;
  projected_northing: number;
  source?: string;
  accuracy_m?: number | null;
}

const CONTROL_POINTS_BASE = "/control-points";
const ADMIN_CONTROL_POINTS_BASE = "/admin/control-points";

export type ListAdminControlPointsParams = PaginationParams &
  SearchParams & {
    regionId?: string;
    provinceId?: string;
    municipalityId?: string;
    srid?: number;
    projectedSrid?: number;
    source?: string;
    hasProjected?: boolean;
    sort?: "code" | "updated_at" | "srid" | "region" | "province";
    order?: "asc" | "desc";
  };

export interface AdminControlPointListResponse
  extends ApiPaginatedResponse<ControlPoint> {
  control_points: ControlPoint[];
}

export async function listControlPoints(accessToken: string): Promise<ControlPoint[]> {
  return authorizedRequest<ControlPoint[]>(accessToken, CONTROL_POINTS_BASE);
}

export async function listAdminControlPoints(
  accessToken: string,
  params: ListAdminControlPointsParams = {}
): Promise<AdminControlPointListResponse> {
  const path =
    ADMIN_CONTROL_POINTS_BASE +
    buildQueryString({
      query: params.query ?? params.search,
      region_id: params.regionId,
      province_id: params.provinceId,
      municipality_id: params.municipalityId,
      srid: params.srid,
      projected_srid: params.projectedSrid,
      source: params.source,
      has_projected: params.hasProjected,
      limit: params.limit,
      offset: params.offset,
      sort: params.sort,
      order: params.order,
    });

  const response = await authorizedRequest<AdminControlPointListResponse>(
    accessToken,
    path
  );
  return {
    control_points: response.control_points ?? [],
    total: response.total ?? 0,
    limit: response.limit ?? params.limit ?? 0,
    offset: response.offset ?? params.offset ?? 0,
    items: response.control_points ?? [],
  };
}

export async function createControlPoint(
  accessToken: string,
  payload: CreateControlPointPayload
): Promise<ControlPoint> {
  return authorizedRequest<ControlPoint>(accessToken, ADMIN_CONTROL_POINTS_BASE, {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export async function updateControlPoint(
  accessToken: string,
  controlPointId: string,
  payload: UpdateControlPointPayload
): Promise<ControlPoint> {
  return authorizedRequest<ControlPoint>(
    accessToken,
    `${ADMIN_CONTROL_POINTS_BASE}/${controlPointId}`,
    {
      method: "PUT",
      body: JSON.stringify(payload)
    }
  );
}

export async function deleteControlPoint(
  accessToken: string,
  controlPointId: string
): Promise<void> {
  await authorizedRequest(
    accessToken,
    `${ADMIN_CONTROL_POINTS_BASE}/${controlPointId}`,
    {
      method: "DELETE"
    }
  );
}

export async function getControlPoint(
  accessToken: string,
  controlPointId: string
): Promise<ControlPoint> {
  return authorizedRequest<ControlPoint>(
    accessToken,
    `${CONTROL_POINTS_BASE}/${controlPointId}`
  );
}
